import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  type Comment,
  type CommentEntityKind,
  entityCommentsQueryOptions,
} from '@/entities/comment'
import { useSession } from '@/entities/session'
import {
  CommentComposer,
  useDeleteComment,
  useEditComment,
  useResolveComment,
} from '@/features/comment-entity'
import { pluralRu } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { DateStamp } from '@/shared/ui/date-stamp'
import { Textarea } from '@/shared/ui/field'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'
import { ZoneNote } from '@/shared/ui/zone-badge'

interface CommentThreadProps {
  entityKind: CommentEntityKind
  entityId: string
}

// Тред комментариев сущности в виде «тем»: корневые — темы (новые сверху),
// ответы свёрнуты за счётчиком «N ответов». Роль смотрящего берём из сессии.
export function CommentThread({ entityKind, entityId }: CommentThreadProps) {
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'
  const currentActorId = session?.id ?? ''

  const commentsQ = useQuery(entityCommentsQueryOptions(entityKind, entityId))
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())

  if (commentsQ.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }
  if (commentsQ.error) {
    return <p className="text-sm text-text-tertiary">Не удалось загрузить комментарии.</p>
  }

  const comments = commentsQ.data
  // Темы — новые сверху; ответы внутри темы — хронологически.
  const roots = comments
    .filter((c) => !c.parent_id)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
  const repliesOf = (id: string) =>
    comments
      .filter((c) => c.parent_id === id)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))

  function expand(rootId: string) {
    setExpanded((prev) => new Set(prev).add(rootId))
  }
  function toggle(rootId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(rootId)) next.delete(rootId)
      else next.add(rootId)
      return next
    })
  }
  // Открытие композера ответа разворачивает тему — свой ответ виден сразу.
  function openReply(rootId: string) {
    setReplyTo((prev) => (prev === rootId ? null : rootId))
    expand(rootId)
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="label text-text-secondary">Комментарии</h2>
        {/* Время у каждого комментария — момент; подписываем ветку один раз. */}
        <ZoneNote />
      </div>

      <CommentComposer entityKind={entityKind} entityId={entityId} isEmployee={isEmployee} />

      {roots.length === 0 ? (
        <p className="text-sm text-text-tertiary">Пока нет комментариев.</p>
      ) : (
        <ul className="flex flex-col gap-4">
          {roots.map((c) => {
            const replies = repliesOf(c.id)
            const open = expanded.has(c.id)
            return (
              <li key={c.id} className="flex flex-col gap-3">
                <CommentItem
                  comment={c}
                  entityKind={entityKind}
                  entityId={entityId}
                  isEmployee={isEmployee}
                  own={c.author.id === currentActorId}
                  onReply={() => openReply(c.id)}
                />

                {replies.length > 0 && (
                  <button
                    type="button"
                    onClick={() => toggle(c.id)}
                    className="self-start pl-11 text-xs text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
                  >
                    {open
                      ? 'Скрыть ответы'
                      : `${replies.length} ${pluralRu(replies.length, 'ответ', 'ответа', 'ответов')}`}
                  </button>
                )}

                {((open && replies.length > 0) || replyTo === c.id) && (
                  <ul className="ml-11 flex flex-col gap-3 border-border-subtle border-l pl-4">
                    {open &&
                      replies.map((r) => (
                        <li key={r.id}>
                          <CommentItem
                            comment={r}
                            entityKind={entityKind}
                            entityId={entityId}
                            isEmployee={isEmployee}
                            own={r.author.id === currentActorId}
                          />
                        </li>
                      ))}
                    {replyTo === c.id && (
                      <li>
                        <CommentComposer
                          entityKind={entityKind}
                          entityId={entityId}
                          parentId={c.id}
                          isEmployee={isEmployee}
                          parentInternal={c.internal}
                          submitLabel="Ответить"
                          placeholder="Ваш ответ…"
                          onDone={() => setReplyTo(null)}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

interface CommentItemProps {
  comment: Comment
  entityKind: CommentEntityKind
  entityId: string
  isEmployee: boolean
  own: boolean
  onReply?: () => void
}

function CommentItem({
  comment,
  entityKind,
  entityId,
  isEmployee,
  own,
  onReply,
}: CommentItemProps) {
  const editM = useEditComment(entityKind, entityId)
  const resolveM = useResolveComment(entityKind, entityId)
  const deleteM = useDeleteComment(entityKind, entityId)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(comment.text)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Правка — только автор. Удаление шире: комментарий сотрудника снимает любой
  // сотрудник, комментарий заказчика — только он сам.
  const canDelete = own || (isEmployee && comment.author.kind === 'employee')

  function saveEdit() {
    if (!draft.trim()) return
    editM.mutate(
      { commentId: comment.id, text: draft.trim() },
      {
        onSuccess: () => setEditing(false),
        onError: () => toast.error('Не удалось сохранить'),
      },
    )
  }

  return (
    <article className="flex gap-3">
      <Avatar name={comment.author.name || '—'} size="sm" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-text-primary">
            {comment.author.name || '—'}
          </span>
          <span className="text-xs text-text-tertiary">
            <DateStamp value={comment.created_at} />
            {comment.at_version != null ? ` · к версии ${comment.at_version}` : ''}
          </span>
          {comment.internal && (
            <Chip tone="warning" variant="soft">
              внутренний
            </Chip>
          )}
          {comment.resolved && (
            <Chip tone="success" variant="soft">
              решён
            </Chip>
          )}
          {comment.parent_deleted && (
            <span className="text-xs text-text-tertiary">(ответ на удалённый)</span>
          )}
        </div>

        {editing ? (
          <div className="flex flex-col gap-2">
            <Textarea
              aria-label="Редактировать комментарий"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-16"
            />
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Отмена
              </Button>
              <Button size="sm" onClick={saveEdit} disabled={editM.isPending}>
                Сохранить
              </Button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm text-text-secondary">{comment.text}</p>
        )}

        {!editing && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-text-tertiary">
            {onReply && (
              <button
                type="button"
                onClick={onReply}
                className="transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                Ответить
              </button>
            )}
            {(isEmployee || own) && (
              <button
                type="button"
                disabled={resolveM.isPending}
                onClick={() =>
                  resolveM.mutate({ commentId: comment.id, resolved: !comment.resolved })
                }
                className="transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                {comment.resolved ? 'Вернуть' : 'Решить'}
              </button>
            )}
            {own && (
              <button
                type="button"
                onClick={() => {
                  setDraft(comment.text)
                  setEditing(true)
                }}
                className="transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                Изменить
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                disabled={deleteM.isPending}
                onClick={() => setConfirmDelete(true)}
                className="text-accent transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                Удалить
              </button>
            )}
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          open
          title="Удалить комментарий?"
          confirmLabel="Удалить"
          pending={deleteM.isPending}
          onClose={() => setConfirmDelete(false)}
          onConfirm={() =>
            deleteM.mutate(comment.id, {
              onSuccess: () => setConfirmDelete(false),
              onError: () => toast.error('Не удалось удалить'),
            })
          }
        >
          Комментарий исчезнет из обсуждения без возврата; ответы на него останутся с пометкой
          «ответ на удалённый».
        </ConfirmDialog>
      )}
    </article>
  )
}
